import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DigestAsk, outcomesApi } from '../../api/services';
import { CVD_BLUE, CVD_ORANGE } from '../../utils/cvdPalette';

// RAW drawdown chart, sc-6127 revision of the sc-5875 chart: the digest rail
// (top, ▼ per ask with the recommended qty, colored by fidelity to the ask),
// order arrows growing from the on-hand step (base = on-hand at placement,
// tip = on-hand + units, so an as-sent restock ask tops out at the max line;
// blue = placed by the digest confirm deadline, orange = late or ad-hoc; bold
// label = expedited), the raw on-hand step line, and the weekly (7-day)
// demand line — hand-rolled SVG (the approved mockup's renderer), replacing
// the @mui/x-charts composition whose reference-line grammar couldn't draw
// triangles or arrows. Still deliberately UNCORRECTED pass-through data, and
// still behind the stable raw stock-series endpoint (spec I8): asks and
// orders ride in as props from the already-loaded review payload, so this
// component fetches exactly what the old one fetched.

export interface OrderEvent {
  date: string; // ISO yyyy-mm-dd (order placement, Central)
  units: number; // the group's units on that day (same-day orders merged)
  byDeadline: boolean;
  expedited: boolean;
}

const INK = '#333';
const INK2 = '#666';
const LINE = '#555';
const GRID = '#ececea';
const CONSUMPTION = '#7b1fa2'; // weekly mass-flow demand signal
const IGNORED = '#a8a8a8';
const ASK_FILL: Record<DigestAsk['outcome'], string> = {
  as_sent: CVD_BLUE,
  off_plan: CVD_ORANGE,
  ignored: IGNORED,
};
const CHART_H = 330;
const BAND_H = 44; // digest rail strip above the plot
const MARGIN = { l: 40, r: 14, t: 8, b: 58 };
// The demand line's window: one week — the weekly demand the min (p95 of
// weekly demand) is computed from (sc-6440; was the retired protection window).
const DEMAND_WINDOW_DAYS = 7;

const toDate = (iso: string): Date => new Date(`${iso}T00:00:00Z`);
const isoDay = (d: Date): string => d.toISOString().slice(0, 10);
// Monday-of-week, matching the estimator's monday_of (wday 0 = Sunday).
const mondayOf = (d: Date): Date => {
  const x = new Date(d);
  x.setUTCDate(x.getUTCDate() - ((x.getUTCDay() + 6) % 7));
  return x;
};

// Human note for an ask triangle's tooltip — composed from the structured
// fields so the tooltip and the classification can't disagree.
export const askNote = (ask: DigestAsk): string => {
  if (ask.outcome === 'ignored') return 'no order before the next ask';
  if (ask.outcome === 'as_sent') return `ordered ${ask.ordered_units} by the confirm deadline`;
  const parts = [`ordered ${ask.ordered_units} (vs ${ask.asked_units} asked)`];
  if (ask.order_count > 1) parts.push(`split across ${ask.order_count} orders`);
  if (ask.late) parts.push('late');
  return parts.join(', ');
};

export const GroupDrawdownChart: React.FC<{
  locationId: number;
  productEquivalencyId: number;
  orders: OrderEvent[];
  asks: DigestAsk[];
}> = ({ locationId, productEquivalencyId, orders, asks }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(1100);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.clientWidth || 1100);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['outcomes', 'group-chart', locationId, productEquivalencyId],
    queryFn: () => outcomesApi.groupChart(locationId, productEquivalencyId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Typography variant="body2" color="error" sx={{ p: 1 }}>
        Couldn’t load the drawdown series — try again.
      </Typography>
    );
  }
  if (!data || data.series.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No sensor data for this group.
      </Typography>
    );
  }

  // Fixed domain: window start clipped to first sensor coverage — the same
  // rule at every location, decoupled from what happens to be plotted.
  const firstCovered = [...data.covered_dates, ...data.series.map((p) => p.date)].sort()[0];
  const x0d = firstCovered && firstCovered > data.since ? firstCovered : data.since;
  const x0 = toDate(x0d).getTime();
  const x1 = toDate(data.until).getTime();
  const span = Math.max(1, x1 - x0);
  const plotTop = MARGIN.t + BAND_H;
  const plotBot = CHART_H - MARGIN.b;
  const X = (iso: string) => MARGIN.l + ((toDate(iso).getTime() - x0) / span) * (width - MARGIN.l - MARGIN.r);

  // Weekly demand (a DEMAND_WINDOW_DAYS window), zero-filled across the
  // Monday grid so the line spans quiet weeks.
  const consumptionByDate = new Map(data.consumption.map((c) => [c.date, c.qty]));
  const weekStarts: string[] = [];
  let m = mondayOf(toDate(x0d));
  const lastDay = toDate(data.until);
  while (m <= lastDay) {
    weekStarts.push(isoDay(m));
    m = new Date(m.getTime() + 7 * 86_400_000);
  }
  const demand = weekStarts.map((d) => ({ date: d, qty: consumptionByDate.get(d) ?? 0 }));

  // On-hand step points: series days carry their value; covered-but-absent
  // days are genuine zeros; non-covered gaps are simply bridged by the step.
  const stockMap = new Map(data.series.map((p) => [p.date, p.on_hand]));
  const coveredSet = new Set(data.covered_dates);
  const stockDays = Array.from(new Set([...data.covered_dates, ...data.series.map((p) => p.date)]))
    .filter((d) => d >= x0d)
    .sort();
  const stockPts = stockDays.map((d) => ({ date: d, v: stockMap.get(d) ?? (coveredSet.has(d) ? 0 : null) }))
    .filter((p): p is { date: string; v: number } => p.v != null);

  // Arrows grow from the on-hand step: base = the last step value at or
  // before placement (the stock the order was reacting to; snapshot grain is
  // accepted as-is — a same-day arrival inflates the base, and orders inside
  // a sensor-coverage gap share the last pre-gap value, exactly like the
  // step line that bridges the same gap), tip = base + units — an as-sent
  // restock ask lands its tip on the max line. The 0 seed never survives the
  // loop: the backend bounds series and covered_dates by the same `since`,
  // so stockPts[0].date === x0d <= every shown order's date.
  const shownOrders = orders.filter((o) => o.date >= x0d);
  const onHandAt = (iso: string): number => {
    let v = 0;
    for (const p of stockPts) {
      if (p.date > iso) break;
      v = p.v;
    }
    return v;
  };

  const peak = Math.max(
    4,
    ...stockPts.map((p) => p.v),
    ...shownOrders.map((o) => onHandAt(o.date) + o.units),
    ...demand.map((d) => d.qty),
    data.current_max ?? 0,
    data.recommended_max ?? 0,
  );
  const yMax = Math.ceil((peak * 1.25) / 5) * 5;
  const Y = (v: number) => plotBot - (v / yMax) * (plotBot - plotTop);

  // Grid: y ticks + month lines + Monday minors.
  const yStep = yMax > 30 ? 20 : 10;
  const yTicks: number[] = [];
  for (let v = 0; v <= yMax; v += yStep) yTicks.push(v);
  const months: { iso: string; label: string }[] = [];
  const mCur = toDate(x0d);
  mCur.setUTCDate(1);
  mCur.setUTCMonth(mCur.getUTCMonth() + 1);
  while (mCur.getTime() <= x1) {
    months.push({ iso: isoDay(mCur), label: mCur.toLocaleDateString(undefined, { month: 'short', timeZone: 'UTC' }) });
    mCur.setUTCMonth(mCur.getUTCMonth() + 1);
  }
  const mondays: number[] = [];
  const mon = mondayOf(toDate(x0d));
  mon.setUTCDate(mon.getUTCDate() + 7);
  for (let t = mon.getTime(); t <= x1; t += 7 * 86_400_000) {
    mondays.push(MARGIN.l + ((t - x0) / span) * (width - MARGIN.l - MARGIN.r));
  }

  // On-hand step path (+ wash below it).
  let stockPath = '';
  let prevY: number | null = null;
  stockPts.forEach((p, i) => {
    const px = X(p.date);
    const py = Y(p.v);
    stockPath += i === 0 ? `M${px} ${py}` : `L${px} ${prevY}L${px} ${py}`;
    prevY = py;
  });
  const rightX = width - MARGIN.r;
  const lastV = stockPts[stockPts.length - 1].v;
  // Single source for the right-edge reference labels — rendered below AND
  // measured for the qty-label clamp, so the two can't drift apart.
  const maxLabel = data.current_max != null ? `max ${data.current_max}` : null;
  const recLabel = data.recommended_max != null && data.recommended_max !== data.current_max
    ? `rec ${data.recommended_max}` : null;

  // Demand step path (subdued purple, variant B of the approved mockup).
  let demandPath = '';
  demand.forEach((c, i) => {
    const px = Math.max(MARGIN.l, X(c.date));
    const py = Y(c.qty);
    demandPath += (i ? 'L' : 'M') + `${px} ${py}`;
    const nx = i + 1 < demand.length ? X(demand[i + 1].date) : rightX;
    demandPath += `L${nx} ${py}`;
  });

  // Order-arrow geometry. Qty label above the tip (like the rail's
  // qty-above-▼) — beside the tip it collides with the right-anchored
  // max/rec labels whenever a recent order lands near the max line. The
  // other plot text lives in two fixed columns (max/rec/on-hand labels hug
  // the right edge, tick text the left), so each label's x is clamped clear
  // of both, with bounds sized from the actual strings (estimated glyph
  // width — no layout API in SVG/jsdom).
  const textW = (s: string) => s.length * 6.6; // ~digit width at fontSize 11
  const refW = Math.max(
    maxLabel ? textW(maxLabel) : 0,
    recLabel ? textW(recLabel) : 0,
    textW(`${lastV}`),
  );
  const orderGeo = shownOrders.map((o) => {
    const px = X(o.date);
    const base = onHandAt(o.date);
    const tipY = Y(base + o.units);
    const w = textW(`+${o.units}`);
    const lo = MARGIN.l + w / 2 + 8;
    return {
      o, px, tipY, w,
      baseY: Y(base),
      // Math.max keeps the bounds ordered (and the label on-canvas) if a
      // degenerately narrow chart pushes the right bound past lo.
      labelX: Math.min(Math.max(px, lo), Math.max(rightX - refW - 6 - w / 2, lo)),
      labelY: tipY - 5,
    };
  });
  // Stagger: a label conflicts with an already-placed label only when their
  // x-spans actually overlap (NOT cluster adjacency — chained neighbors can
  // sit 15px apart each yet 45px apart end-to-end and need no separation).
  // On conflict, jump above every conflicting row; the bump (12) exceeds the
  // collision gate (11) so one jump clears the set. Lifts stop at the plot
  // top so a same-spot burst piles at the ceiling instead of invading the
  // tick numbers or the digest rail — arrows keep their own 25% headroom,
  // labels get this ceiling.
  const placed: { x: number; y: number; w: number }[] = [];
  orderGeo.forEach((g) => {
    const conflicts = placed.filter((p) => Math.abs(g.labelX - p.x) < (g.w + p.w) / 2 + 4);
    if (conflicts.some((p) => Math.abs(g.labelY - p.y) < 11)) {
      g.labelY = Math.max(Math.min(...conflicts.map((p) => p.y)) - 12, plotTop + 8);
    }
    placed.push({ x: g.labelX, y: g.labelY, w: g.w });
  });

  const bandY = MARGIN.t + 24;
  const legendY = CHART_H - 8;

  // Legend: fixed-width items, every mark type represented.
  const legendTri = (x: number, fill: string) =>
    `M${x + 1} ${legendY - 10}L${x + 13} ${legendY - 10}L${x + 7} ${legendY - 1}Z`;
  const legendArrow = (x: number) =>
    `M${x + 2} ${legendY - 10}L${x + 10} ${legendY - 10}L${x + 6} ${legendY - 15}Z`;
  let lx = MARGIN.l;
  const legendItems: React.ReactNode[] = [];
  const pushLegend = (draw: (x: number) => React.ReactNode, label: string, gap: number) => {
    const x = lx;
    legendItems.push(
      <g key={label}>
        {draw(x)}
        <text x={x + 18} y={legendY} fill={INK2}>{label}</text>
      </g>,
    );
    lx += gap;
  };
  pushLegend((x) => <line x1={x} y1={legendY - 4} x2={x + 13} y2={legendY - 4} stroke={LINE} strokeWidth={2} />, 'on hand', 86);
  pushLegend(
    (x) => <line x1={x} y1={legendY - 4} x2={x + 13} y2={legendY - 4} stroke={CONSUMPTION} strokeWidth={1.8} opacity={0.55} />,
    `${DEMAND_WINDOW_DAYS}-day demand`, 122,
  );
  legendItems.push(<text key="dg" x={lx} y={legendY} fill={INK2}>digest:</text>);
  lx += 46;
  pushLegend((x) => <path d={legendTri(x, CVD_BLUE)} fill={CVD_BLUE} />, 'ordered as sent', 122);
  pushLegend((x) => <path d={legendTri(x, CVD_ORANGE)} fill={CVD_ORANGE} />, 'late / qty differed', 138);
  pushLegend((x) => <path d={legendTri(x, IGNORED)} fill={IGNORED} />, 'ignored', 82);
  pushLegend(
    (x) => (
      <g>
        <line x1={x + 6} y1={legendY} x2={x + 6} y2={legendY - 11} stroke={CVD_BLUE} strokeWidth={2.4} />
        <path d={legendArrow(x)} fill={CVD_BLUE} />
      </g>
    ),
    'order by deadline', 138,
  );
  pushLegend(
    (x) => (
      <g>
        <line x1={x + 6} y1={legendY} x2={x + 6} y2={legendY - 11} stroke={CVD_ORANGE} strokeWidth={2.4} />
        <path d={legendArrow(x)} fill={CVD_ORANGE} />
      </g>
    ),
    'late / ad-hoc order', 138,
  );
  legendItems.push(
    <text key="bold" x={lx} y={legendY} fill={INK2}>
      <tspan fontWeight={700}>bold</tspan> = expedited
    </text>,
  );

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <svg width={width} height={CHART_H} fontSize={11} role="img" aria-label="Group drawdown chart">
        {/* grid */}
        {yTicks.map((v) => (
          <g key={`y${v}`}>
            <line x1={MARGIN.l} y1={Y(v)} x2={rightX} y2={Y(v)} stroke={GRID} />
            <text x={MARGIN.l - 6} y={Y(v) + 3.5} textAnchor="end" fill={INK2}>{v}</text>
          </g>
        ))}
        {months.map((mo) => (
          <g key={mo.iso}>
            <line x1={X(mo.iso)} y1={plotTop} x2={X(mo.iso)} y2={plotBot} stroke={GRID} />
            <text x={X(mo.iso)} y={plotBot + 16} textAnchor="middle" fill={INK2}>{mo.label}</text>
          </g>
        ))}
        {mondays.map((px) => (
          <line key={`mon${px}`} x1={px} y1={plotBot} x2={px} y2={plotBot + 3} stroke="#ccc" />
        ))}

        {/* reference lines: max dark gray dashed, rec CVD blue dashed */}
        {data.current_max != null && (
          <g>
            <line x1={MARGIN.l} y1={Y(data.current_max)} x2={rightX} y2={Y(data.current_max)}
              stroke={INK} strokeDasharray="6 3" />
            <text x={rightX - 4} y={Y(data.current_max) + 13} textAnchor="end" fill={INK}>
              {maxLabel}
            </text>
          </g>
        )}
        {data.recommended_max != null && data.recommended_max !== data.current_max && (
          <g>
            <line x1={MARGIN.l} y1={Y(data.recommended_max)} x2={rightX} y2={Y(data.recommended_max)}
              stroke={CVD_BLUE} strokeWidth={1.4} strokeDasharray="8 4" />
            <text x={rightX - 4} y={Y(data.recommended_max) + 13} textAnchor="end" fill={CVD_BLUE}>
              {recLabel}
            </text>
          </g>
        )}

        {/* demand line */}
        {demandPath && <path d={demandPath} fill="none" stroke={CONSUMPTION} strokeWidth={1.6} opacity={0.45} />}

        {/* on-hand step + wash */}
        <path d={`${stockPath}L${rightX} ${prevY}`} fill="none" stroke={LINE} strokeWidth={2} strokeLinejoin="round" />
        <path d={`${stockPath}L${rightX} ${prevY}L${rightX} ${Y(0)}L${X(stockPts[0].date)} ${Y(0)}Z`}
          fill={CVD_BLUE} opacity={0.07} />
        <text x={rightX - 2} y={Y(lastV) - 6} textAnchor="end" fontWeight={700} fill={INK}>{lastV}</text>

        {/* order arrows from the on-hand step */}
        {orderGeo.map(({ o, px, tipY, baseY, labelX, labelY }) => {
          const c = o.byDeadline ? CVD_BLUE : CVD_ORANGE;
          return (
            <g key={`o${o.date}`}>
              <title>{`${o.date} — ${o.units} unit${o.units === 1 ? '' : 's'}${o.expedited ? ' — expedited' : ''}`}</title>
              <line x1={px} y1={baseY} x2={px} y2={tipY + 7} stroke={c} strokeWidth={2.4} />
              <path d={`M${px - 4.5} ${tipY + 8}L${px + 4.5} ${tipY + 8}L${px} ${tipY}Z`} fill={c} />
              <text x={labelX} y={labelY} textAnchor="middle" fill={INK} fontWeight={o.expedited ? 700 : 400}>+{o.units}</text>
            </g>
          );
        })}

        {/* digest rail */}
        <text x={MARGIN.l} y={MARGIN.t + 10} fill={INK2}>Digest asked:</text>
        {asks.filter((a) => a.date >= x0d).map((a) => {
          const px = X(a.date);
          const fill = ASK_FILL[a.outcome];
          return (
            <g key={`a${a.digest_id}`}>
              <title>{`${a.date} — digest recommended ${a.asked_units} — ${askNote(a)}`}</title>
              <path d={`M${px - 5.5} ${bandY}L${px + 5.5} ${bandY}L${px} ${bandY + 9}Z`}
                fill={fill} stroke={fill} strokeWidth={1.8} strokeLinejoin="round" />
              <text x={px} y={bandY - 5} textAnchor="middle" fill={INK2}>{a.asked_units}</text>
            </g>
          );
        })}

        {/* legend */}
        {legendItems}
      </svg>
    </div>
  );
};
